<script setup lang="ts">
import Modal from "@/Components/Modal.vue";
import {onMounted, ref, watch} from "vue";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import {SEARCH_STATUS_RESULTS, useRobloxUsernameSearch} from "@/roblox/search";
import {LOCALES} from "@/constants.js";

const props = defineProps<{
    isOpen: boolean;
    robux_amount?: number;
    selected_user?: {
        id: number;
        name: string;
        displayName: string;
        hasVerifiedBadge: boolean;
        avatarUrl?: string;
    } | null;
}>();
defineEmits<{
    (e: 'onToggle', payload: boolean): void
}>()

// Поиск (status/searchResults/isLoading) живет в общем composable — он же
// отменяет устаревшие запросы, чтобы не жечь лимиты Roblox на каждый символ
const {status, searchResults, isLoading, debouncedSearch, reset: resetSearch} = useRobloxUsernameSearch(3);

const nickname = ref('');
const selectedUser = ref(null);

const invalidCharError = ref(false);
let isSanitizing = false; // предотвращает мгновенный сброс ошибки после программной замены

// Отслеживание изменений в поле ввода
watch(nickname, (newValue) => {
    if (isSanitizing) { // второй вызов после программного изменения
        isSanitizing = false;
        return;
    }

    if (!newValue.length) { // пользователь очистил поле
        invalidCharError.value = false;
        resetSearch();
        selectedUser.value = null;
        return;
    }

    // Разрешены только латинские буквы, цифры и underscore
    const hasInvalid = /[^a-zA-Z0-9_]/.test(newValue);
    if (hasInvalid) {
        invalidCharError.value = true; // показываем сообщение
        isSanitizing = true;
        nickname.value = newValue.replace(/[^a-zA-Z0-9_]/g, ''); // удаляем все недопустимые символы
        return; // ждём следующий цикл
    }

    if (invalidCharError.value) { // пользователь снова ввёл валидный символ
        invalidCharError.value = false;
    }

    debouncedSearch(newValue);
});

// Функция для выбора пользователя
const selectUser = (user) => {
    selectedUser.value = user;
};

onMounted(() => {
    if (props?.selected_user) {
        // Аккаунт уже выбран — показываем его сразу, без похода в Roblox.
        // isSanitizing гасит реакцию watcher на программную установку поля.
        isSanitizing = true;
        nickname.value = props.selected_user.name;
        selectedUser.value = props.selected_user;
        searchResults.value = [props.selected_user];
        status.value = SEARCH_STATUS_RESULTS;
    }
});

const onLogin = () => {
    if (!selectedUser.value) return;
    router.post(route('roblox.login'), {user_id: selectedUser.value.id}, {
        onSuccess: () => {
            // refresh page to update auth state
            window.location.reload();
        }
    });
};


</script>

<template>
    <Modal :show="isOpen" max-width="md" modalClass="h-[90dvh]">
        <div class="py-5 px-3 flex flex-col gap-3 bg-slate-200 z-20 h-full min-h-0">
            <div class="w-full flex justify-between pb-6 shrink-0">
                <p class="text-xl font-sans uppercase text-black font-medium">
                    {{$t('Log in')}}
                </p>
                <button @click="$emit('onToggle', false)">
                    <img src="/assets/img/roblox/close.svg" alt="close">
                </button>
            </div>
            <div class="flex flex-col h-full min-h-0">
                <div class="flex flex-col gap-5 mt-5 w-full flex-1 min-h-0">
                    <div class="w-full shrink-0">
                        <p class="uppercase text-lg font-sans font-medium text-black px-2.5">
                            {{ $t('Roblox nickname:') }}
                        </p>
                        <div class="h-[52px] mt-2.5 max-w-full w-full px-4 border-1 border-slate-200 bg-white items-center flex rounded-lg">
                            <img v-if="nickname.length" src="/assets/img/roblox/at-black.svg" alt="at" />
                            <img v-else src="/assets/img/roblox/at-slate-500.svg" alt="at" />
                            <input v-model="nickname" class="bg-transparent placeholder:font-semibold placeholder:text-xl placeholder:text-slate-500 placeholder:font-sans w-full font-sans font-bold text-2xl h-full border-none focus:ring-0 outline-hidden"
                                   :placeholder="$t('Enter Roblox username')"
                                   type="text">
                        </div>
                        <p v-if="invalidCharError" class="font-sans mt-2.5 font-medium text-sm text-red-400">
                            {{$t('Only Latin letters, numbers and underscore are allowed')}}
                        </p>
                    </div>

                    <!-- Прокручиваемая зона результатов -->
                    <div class="flex-1 min-h-0 relative">
                        <template v-if="status === 0">
                            <img
                                alt="nickname-img"
                                class="w-full h-full object-contain rounded-lg"
                                :src="$page.props.lang === LOCALES.RU ? '/assets/img/roblox/nickname-hires-ru.webp' : '/assets/img/roblox/nickname-hires-en.webp'"
                            >
                        </template>
                        <template v-else-if="status === 1">
                            <div
                                class="max-w-full grid px-2.5 grid-cols-2 md:grid-cols-3 bg-white rounded-lg w-full h-full overflow-y-auto">
                                <button
                                    v-for="user in searchResults"
                                    :key="user.id"
                                    :class="[
                                        'relative transition-all flex items-center flex-col gap-2 border-white border-r w-full h-[112px]',
                                        selectedUser?.id === user.id
                                            ? 'bg-green-300 opacity-100 border-2 border-green-500'
                                            : 'opacity-70 hover:bg-green-300 hover:opacity-100 bg-slate-200 border-b'
                                    ]"
                                    @click="selectUser(user)"
                                >
                                    <img
                                        :alt="user.name"
                                        :src="user.avatarUrl || '/assets/img/roblox/roblox-man.webp'"
                                        class="rounded-full"
                                        style="width: 60px; height: 60px"
                                    >
                                    <p class="text-black font-sans text-sm font-medium text-center">
                                        {{ user.displayName || user.name }}
                                    </p>
                                    <div v-if="selectedUser?.id === user.id" class="absolute top-1 right-1">
                                        <svg fill="none" height="20" viewBox="0 0 20 20" width="20"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="10" cy="10" fill="#10B981" r="10"/>
                                            <path d="M7 10L9 12L13 8" stroke="white" stroke-linecap="round"
                                                  stroke-linejoin="round"
                                                  stroke-width="2"/>
                                        </svg>
                                    </div>
                                </button>
                            </div>
                        </template>
                        <template v-else-if="status === 2">
                            <div
                                class="max-w-full flex gap-2 flex-col justify-center items-center px-2.5 h-full bg-white rounded-lg w-full overflow-hidden">
                                <img alt="roblox-man" src="/assets/img/roblox/roblox-man.webp"
                                     style="width: 60px; height: 60px">
                                <p class="font-sans font-medium text-base">
                                    {{ $t('Looking for an account...') }}
                                </p>
                            </div>
                        </template>
                        <template v-else-if="status === 3">
                            <div
                                class="max-w-full flex gap-2 flex-col justify-center items-center px-2.5 bg-white h-full rounded-lg w-full">
                                <img alt="error" src="/assets/img/roblox/user-not-found.svg" />
                                <p class="font-sans font-medium text-base">
                                    {{ $t('Account not found') }}
                                </p>
                                <p class="text-center font-sans text-base">
                                    {{ $t('Check if nickname you entered is correct') }}
                                </p>
                            </div>
                        </template>
                    </div>

                    <button
                        @click="onLogin"
                        :class="[
                            'py-4 uppercase mt-5 font-sans font-medium text-sm flex justify-center text-white rounded-full transition-all box-content shrink-0',
                            selectedUser
                                ? 'bg-violet-500 hover:opacity-60 cursor-pointer'
                                : 'bg-violet-300 cursor-not-allowed'
                        ]"
                        :disabled="!selectedUser"
                    >
                        {{ $t('Log in') }}
                    </button>
                </div>
            </div>
        </div>
    </Modal>
</template>
